// Nexora — composant de signature manuscrite, porté depuis
// StoreFlowProV2/public/modules/signature-pad.js (premier composant de ce type dans le
// projet vanilla, réutilisé ici tel quel pour la validation Arbeitszeitliste). Pointer
// Events unifiés (doigt/souris/stylet), vérifie qu'un vrai trait a été dessiné (distance
// cumulée, pas un simple tap), jamais pré-remplie.
//
// Différence avec le vanilla : la couleur du trait est passée en option
// (`strokeColor`) au lieu d'être lue depuis une variable CSS --sfds-ink — nexora-app
// n'utilise pas de custom properties CSS globales, tout passe par les tokens JS (P.ink).
export function createSignaturePad(container, options = {}) {
  const MIN_STROKE_LENGTH = 24; // px cumulés — en-dessous, considéré comme un simple clic/tap

  container.innerHTML = `
    <div class="nx-sign-pad-wrap" style="position:relative;border-radius:16px;overflow:hidden;background:#fff;border:1.5px solid ${options.borderColor || "#e4ddcd"};height:${options.height || 160}px;touch-action:none;">
      <canvas class="nx-sign-canvas" aria-label="${options.ariaLabel || "Zone de signature"}" style="width:100%;height:100%;display:block;touch-action:none;"></canvas>
      <div class="nx-sign-hint" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:12.5px;color:#8f8571;pointer-events:none;">${options.hintText || ""}</div>
    </div>
    <div class="nx-sign-actions" style="display:flex;gap:8px;margin-top:8px;">
      <button type="button" data-action="clear" style="flex:1;border:0;padding:9px 0;border-radius:99px;background:${options.softBg || "#f3ede0"};color:${options.inkColor || "#211d16"};font-weight:700;font-size:12.5px;">${options.clearLabel || "Effacer"}</button>
    </div>
  `;

  const canvas = container.querySelector(".nx-sign-canvas");
  const hint = container.querySelector(".nx-sign-hint");
  const ctx = canvas.getContext("2d");

  let drawing = false;
  let strokeLength = 0;
  let hasInk = false;
  let lastX = 0;
  let lastY = 0;
  let dpr = 1;

  function resizeCanvas() {
    const rect = canvas.getBoundingClientRect();
    dpr = window.devicePixelRatio || 1;
    const prev = hasInk ? canvas.toDataURL() : null;
    canvas.width = Math.max(1, Math.round(rect.width * dpr));
    canvas.height = Math.max(1, Math.round(rect.height * dpr));
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = options.strokeColor || "#211d16";
    if (prev) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = prev;
    }
  }

  function pointerPos(evt) {
    const rect = canvas.getBoundingClientRect();
    return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
  }

  function onPointerDown(evt) {
    evt.preventDefault();
    canvas.setPointerCapture(evt.pointerId);
    drawing = true;
    const { x, y } = pointerPos(evt);
    lastX = x; lastY = y;
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function onPointerMove(evt) {
    if (!drawing) return;
    evt.preventDefault();
    const { x, y } = pointerPos(evt);
    ctx.lineTo(x, y);
    ctx.stroke();
    strokeLength += Math.hypot(x - lastX, y - lastY);
    lastX = x; lastY = y;
    if (strokeLength > 2 && !hasInk) {
      hasInk = true;
      hint.style.display = "none";
      options.onChange?.(strokeLength >= MIN_STROKE_LENGTH);
    }
  }

  function onPointerUp(evt) {
    if (!drawing) return;
    drawing = false;
    try { canvas.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }
    options.onChange?.(strokeLength >= MIN_STROKE_LENGTH);
  }

  function clear() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokeLength = 0;
    hasInk = false;
    hint.style.display = "flex";
    options.onChange?.(false);
  }

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointercancel", onPointerUp);
  canvas.addEventListener("touchmove", (e) => { if (drawing) e.preventDefault(); }, { passive: false });

  container.querySelector('[data-action="clear"]').addEventListener("click", clear);

  window.addEventListener("resize", resizeCanvas);
  resizeCanvas();

  return {
    clear,
    hasValidSignature: () => strokeLength >= MIN_STROKE_LENGTH,
    getStrokeLength: () => Math.round(strokeLength),
    toDataURL: () => canvas.toDataURL("image/png"),
    destroy: () => {
      window.removeEventListener("resize", resizeCanvas);
    },
  };
}
